import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../../../app/hooks";
import { Loading } from "../../../../../components/Loading";
import Select from "../../../../../components/input/Select";
import {
	useEssayDraftHistoryReportQuery,
	WriterEssayDraftHistoryViewModel,
} from "../../../shared/progress/essayDraftHistory/essayDraftHistoryReportApiSlice";
import {
	getAverageScoreIncrease,
	getDraftCount,
	getFirstPromptOption,
	isConstructedResponse,
	getPromptOptions,
} from "../../../shared/progress/essayDraftHistory/DraftHistoryUtil";
import { EssayDraftHistorySummary } from "../../../shared/progress/essayDraftHistory/TableUtil";
import EssayDraftHistoryTable from "./EssayDraftHistoryTable";
import EssayScoresByDraftChart from "./EssayDraftHistoryChart";

export const ESSAY_DRAFT_HISTORY_SECTION = "essay-draft-history";

interface EssayDraftHistoryProps {
	courseId?: number;
	writerId?: number;
}

export default function EssayDraftHistory({ courseId, writerId }: EssayDraftHistoryProps) {
	const result = useEssayDraftHistoryReportQuery({ courseId, writerId });
	const data = result.data;

	const [promptFilter, setPromptFilter] = useState<number>(0);
	const handlePromptChange = (promptId: number) => {
		setPromptFilter(promptId);
	};

	const summaries = useMemo(() => toTableSummary(data, promptFilter), [data, promptFilter]);
	const filteredChartData = useMemo(
		() => toChartSummary(data, promptFilter),
		[data, promptFilter]
	);

	// Set prompt filter to be first prompt available from dataset.
	useEffect(() => {
		setPromptFilter(getFirstPromptOption(data ?? []));
	}, [data]);

	return (
		<section id={ESSAY_DRAFT_HISTORY_SECTION} className="flex flex-col mt-8">
			<h2>Essay Draft History</h2>
			<p className="mt-4">
				The report below displays the scores for all your drafts for each prompt's most
				recent essay and the average score increase between each draft. To view your draft
				progress for another prompt, choose a different prompt in the prompt filter.
			</p>
			<Loading loading={result.isLoading} />
			{!result.isLoading && (
				<>
					<div className="flex flex-col mt-4 gap-2 w-full md:flex-row md:gap-4">
						<Select
							value={promptFilter}
							setValue={(v) => handlePromptChange(v as number)}
							label="Prompt Filter"
							simpleListBoxId="promptFilter"
							options={getPromptOptions(data)}
							fieldClasses="w-1/4"
						/>
					</div>
					<EssayDraftHistoryTable data={summaries} />
					<EssayScoresByDraftChart
						data={filteredChartData}
						isConstructedResponse={isConstructedResponse(filteredChartData)}
					/>
				</>
			)}
		</section>
	);
}

// Return filtered and summarized data for table.
function toTableSummary(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	promptFilter: number
): EssayDraftHistorySummary[] {
	if (!data) {
		return [];
	}

	return data
		.map((writer) => {
			const essays = writer.essays ?? [];
			const filteredEssays =
				promptFilter == 0
					? essays
					: essays.filter((essay) => Number(essay.promptId) === Number(promptFilter));

			const essayAverageIncreases = filteredEssays.map((essay) =>
				getAverageScoreIncrease(essay.drafts ?? [])
			);

			const draftAverageIncrease =
				essayAverageIncreases.length > 0
					? Number(
							(
								essayAverageIncreases.reduce((acc, val) => acc + val, 0) /
								essayAverageIncreases.length
							).toFixed(1)
						)
					: 0;

			return {
				writerId: writer.writerId,
				draftCount: getDraftCount(filteredEssays, promptFilter),
				averageIncrease: draftAverageIncrease,
				draftHistory: filteredEssays.flatMap((fe) => fe.drafts),
				promptName: filteredEssays[0]?.promptName,
			};
		})
		.filter((summary) => summary.draftCount > 0);
}

// Return filtered data for chart.
function toChartSummary(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	promptFilter: number
): WriterEssayDraftHistoryViewModel[] {
	if (!data) {
		return [];
	}

	return data
		.map((writer) => {
			const essays = writer.essays ?? [];
			const filteredEssays =
				promptFilter == 0
					? essays
					: essays.filter((essay) => Number(essay.promptId) === Number(promptFilter));
			return { ...writer, essays: filteredEssays };
		})
		.filter((writer) => (writer.essays?.length ?? 0) > 0)
		.sort((a, b) => a.writerFormalFullName.localeCompare(b.writerFormalFullName));
}
